import { getImageSources } from "components/utils/assets";
import React from "react";

export const getCardTypeName = (cardTypeCd) => {
  if (cardTypeCd) {
    return { E: "E-Card", I: "Invite", P: "Photo", G: "Infographic", N: "Animation", S: "Static" }[cardTypeCd];
  }
  return "Unknown";
};

export const getRecipientsText = (recipients, reminderName = "") => {
  const maxRecipients = 2;
  if (recipients?.length > 0) {
    const displayRecipients = recipients.filter((r) => r.recipientName?.trim() || r.recipientEmail).map((r) => r.recipientName?.trim() || r.recipientEmail);
    if (displayRecipients.length > maxRecipients) {
      return [
        ...displayRecipients.slice(0, maxRecipients),
        `${displayRecipients.length - maxRecipients} other${displayRecipients.length - maxRecipients === 1 ? "" : "s"}`,
      ]
        .join(", ")
        .replace(/,(?=[^,]+$)/, " and");
    }
    return displayRecipients.join(", ").replace(/,(?=[^,]+$)/, " and");
  }
  return reminderName;
};

export const getSignersText = (sender, signers, max = 2) => {
  let displaySigners = [];
  if (signers?.length > 0) {
    const completeSigners = signers.filter((s) => s.signedDttm && s.signatureTx && !s.isSender).map((s) => s.signatureTx);
    displaySigners = completeSigners;
    if (max && displaySigners.length > max) {
      displaySigners = displaySigners.slice(0, max);
      displaySigners.push(`${completeSigners.length - displaySigners.length} other(s)`);
    }
  }
  if (sender) {
    displaySigners.unshift(sender);
  }
  return displaySigners.join(", ").replace(/,(?=[^,]+$)/, " and");
};

export const getDeletableCardSignerIds = (signers, userId, memberId, isSender) => {
  const deletableCardSignerIds = [];
  signers?.forEach((signer) => {
    const isMessageUserIdOwner = !!userId && signer?.userId === userId;
    const isMessageMemberIdOwner = !!memberId && signer?.memberId === memberId;
    const isMessageOwner = isMessageUserIdOwner || isMessageMemberIdOwner;
    if (isMessageOwner || isSender) {
      deletableCardSignerIds.push(signer.cardSignerId);
    }
  });
  return deletableCardSignerIds;
};

export const getEditableCardSignerIds = (signers, userId, memberId) => {
  const editableCardSignerIds = [];
  signers?.forEach((signer) => {
    const isMessageUserIdOwner = !!userId && signer?.userId === userId;
    const isMessageMemberIdOwner = !!memberId && signer?.memberId === memberId;
    const isMessageOwner = isMessageUserIdOwner || isMessageMemberIdOwner;
    if (isMessageOwner) {
      editableCardSignerIds.push(signer.cardSignerId);
    }
  });
  return editableCardSignerIds;
};

export const getAssetsOfType = (cardAssets, assetTypeIds, fileExtension = "") => {
  if (cardAssets?.length > 0) {
    let assets = cardAssets.filter((a) => assetTypeIds.some((at) => assetTypeIds.includes(a.s3AssetTypeId))); // filter by asset types
    if (fileExtension) {
      assets = assets.filter((a) => a.fileExtension === fileExtension); //filter by specific file extension
    }
    return assets;
  }
  return [];
};

export const getThumbnailAssets = (card, retry = false) => {
  if (card) {
    const { cardAssets, cardCustom } = card;
    let aspectRatio = 1.77; // 16/9
    let assetTypeIds = [1]; // XXX TODO
    let cardAssetsToUse = [...cardAssets];
    if (cardCustom && cardCustom.cardAssets) {
      assetTypeIds = [5]; // XXX TODO
      cardAssetsToUse = [...cardCustom.cardAssets];
    }
    let imageAssets = getAssetsOfType(cardAssetsToUse, assetTypeIds);
    const { src, srcSet, sources } = getImageSources(imageAssets, retry);
    if (imageAssets?.length > 0) {
      const imageAsset = imageAssets[0];
      aspectRatio = imageAsset.xResolution / imageAsset.yResolution;
    }
    return { src, srcSet, sources, aspectRatio };
  }
  return {};
};

export const getPreviewAssets = (card, withWatermark = false) => {
  if (card) {
    const { cardAssets, isPhoto, cardCustom } = card;
    let aspectRatio = 1.77; // 16/9
    let imageAssets = getAssetsOfType(cardAssets, [1]); // hard-coded until we build a global s3AssetType data
    const gifAssets = getAssetsOfType(cardAssets, [2]); // XXX TODO
    const videoAssets = getAssetsOfType(cardAssets, [4]); // XXX TODO
    if (isPhoto && cardCustom && cardCustom.cardAssets) {
      imageAssets = getAssetsOfType(cardCustom.cardAssets, [5]); // XXX TODO
      if (withWatermark) {
        imageAssets = imageAssets.map((a) => ({ ...a, assetUrl: a.assetUrl.replace("clean-", "watermark-") }));
      }
    }
    if (imageAssets?.length > 0) {
      const imageAsset = imageAssets[0];
      aspectRatio = imageAsset.xResolution / imageAsset.yResolution;
    }
    return { videoAssets, imageAssets, gifAssets, aspectRatio };
  }
  return {};
};

export const getCategoryPreviewAssets = (cardCategory) => {
  if (cardCategory) {
    let aspectRatio = 1.77; // 16/9
    let imageAssets = getAssetsOfType(cardCategory.imageAssets, [8]); // hard-coded until we build a global s3AssetType data
    if (imageAssets?.length > 0) {
      const imageAsset = imageAssets[0];
      aspectRatio = imageAsset.xResolution / imageAsset.yResolution;
    }
    return { imageAssets, aspectRatio };
  }
  return {};
};

const getCardSendControlCenterHeaderProps = (cardSend, isImmediateSending) => {
  const { recipients, sentDttm, isGroupCard } = cardSend;
  const linkRecipients = recipients?.filter((r) => r.recipientManualId === 1) || [];
  const emailRecipients = recipients?.filter((r) => r.recipientManualId !== 1) || [];
  const linkRecipientsText = getRecipientsText(linkRecipients);
  const emailRecipientsText = getRecipientsText(emailRecipients);
  const cardType = isGroupCard ? "Group Card" : "Card";
  const singleRecipient = recipients?.length === 1 ? recipients[0].recipientName : "";
  let title = `Card Details`;
  let subtitle;
  let withDate = false;
  let withCardOptions = false;
  let withShareNowButton = false;
  let sendTextMessageButtonTitle = "Share via text";
  let shareNowButtonTitle = "Share card now";
  let nativeShareOptionsInfo;
  let shareOptionsInfo;
  if (sentDttm || isImmediateSending) {
    if (emailRecipients.length === 0) {
      // SCHEDULED IMMEDIATELY | link recipients only
      title = `Share Your ${cardType}${singleRecipient ? ` with ${singleRecipient}` : ""}`;
      withShareNowButton = true;
      shareOptionsInfo = (
        <>
          <span>’Share Card Now’ to send your card via text or social media</span>
          <br />
          <span className="italic faded small">(We also sent you an email with options to send the card)</span>
        </>
      );
      nativeShareOptionsInfo = <span className="block mobile-tight">We also sent you an email with options to send the card</span>;
    } else if (linkRecipients.length === 0) {
      // SCHEDULED IMMEDIATELY | email recipients only
      title = `Your ${cardType} was Sent`;
      subtitle = "Your card was sent on:";
      withDate = true;
    } else {
      // SCHEDULED IMMEDIATELY | link AND email recipients
      title = `Your ${cardType} is Ready!`;
      subtitle = (
        <>
          <span>Your card was sent via email to {emailRecipientsText}.</span>
          <br />
          <span>Dont forget to share it with {linkRecipientsText}!</span>
        </>
      );
      withShareNowButton = true;
      shareOptionsInfo = (
        <>
          <span>’Share Card Now’ to send your card via text or social media</span>
          <br />
          <span className="italic faded small">(We also sent you an email with options to send the card)</span>
        </>
      );
      nativeShareOptionsInfo = <span>We also sent you an email with options to send the card</span>;
    }
  } else {
    if (emailRecipients.length === 0) {
      // SCHEDULED LATER | link recipients only
      title = (
        <span className={isGroupCard ? "wrap-large" : "wrap"}>
          Your {cardType} is Ready and Waiting{singleRecipient ? ` for ${singleRecipient}` : ""}!
        </span>
      );
      subtitle = "We’ll send you an email reminder with options to send the card on:";
      withDate = true;
      withCardOptions = true;
    } else if (linkRecipients.length === 0) {
      // SCHEDULED LATER | email recipients only
      title = `Your ${cardType} is Scheduled!`;
      subtitle = "Your card will be sent on:";
      withDate = true;
      withCardOptions = true;
    } else {
      // SCHEDULED LATER | link AND email recipients
      title = `Your ${cardType} is Scheduled!`;
      subtitle = (
        <>
          <span>We scheduled the card for {emailRecipientsText}.</span>
          <br />
          <span>We’ll send you an email reminder with options to send the card to {linkRecipientsText} on:</span>
        </>
      );
      withDate = true;
      withCardOptions = true;
    }
  }
  return {
    title,
    subtitle,
    withDate,
    withCardOptions,
    withShareNowButton,
    sendTextMessageButtonTitle,
    nativeShareOptionsInfo,
    shareNowButtonTitle,
    shareOptionsInfo,
  };
};

export const getCardSendControlCenterProps = (location, cardSend, isImmediateSending, reminderName) => {
  const { recipients, sentDttm, isGroupCard, draftInd } = cardSend;
  const isDraft = draftInd === 1;
  const emailRecipients = recipients?.filter((r) => r.recipientManualId !== 1) || [];
  const recipientsText = getRecipientsText(recipients);
  const cardType = isGroupCard ? "Group Card" : "Card";

  let { title, subtitle, withDate, withCardOptions, withShareNowButton, ...rest } = getCardSendControlCenterHeaderProps(cardSend, isImmediateSending);
  let withDelete = false;
  let recipientsToggled = false;
  let withCard = false;
  let withCardDetails = false;

  switch (location) {
    case "card-create-success":
      recipientsToggled = true;
      withCardDetails = true;
      break;
    case "my-cards-item":
      if (sentDttm) {
        title = "Card Details";
        if (emailRecipients.length === 0) {
          subtitle = "Delivery date:";
        }
      }
      withDelete = true;
      withDate = true;
      withShareNowButton = false;
      withCardOptions = true;
      withCardDetails = true;
      break;
    case "my-cards-landing":
      withDelete = true;
      withDate = true;
      withShareNowButton = false;
      withCardOptions = true;
      withCardDetails = true;
      break;
    case "reminders-list-item":
      if (isDraft) {
        title = `${cardType} in progress for ${recipientsText || reminderName}`;
        subtitle = "Pick up where you left off and schedule this card";
      } else if (sentDttm) {
        title = `Your ${cardType} was Sent to ${recipientsText}`;
        subtitle = "";
      } else {
        title = `${cardType} scheduled for ${recipientsText}`;
        subtitle = "";
      }
      withDelete = true;
      withDate = !isDraft;
      withShareNowButton = false;
      withCard = true;
      withCardOptions = true;
      withCardDetails = true;
      break;
    case "reminder-flow":
      if (isDraft) {
        title = `${cardType} in progress for ${recipientsText || reminderName}`;
        subtitle = "Pick up where you left off and schedule this card";
      } else if (sentDttm) {
        title = `Your ${cardType} was Sent to ${recipientsText}`;
        subtitle = "";
      } else {
        title = `${cardType} scheduled for ${recipientsText}`;
        subtitle = "";
      }
      withDelete = true;
      withDate = !isDraft;
      withShareNowButton = false;
      withCard = true;
      withCardOptions = true;
      break;
  }
  return {
    title,
    subtitle,
    withDate,
    withDelete,
    withCard,
    withShareNowButton,
    withCardDetails,
    withCardOptions,
    recipientsToggled,
    ...rest,
  };
};

export const getCardWidthPercentage = (aspectRatio, widthVariant, breakpoint = "") => {
  let widthPercentage = 100;

  if (widthVariant === "thumbnail") {
    if (aspectRatio <= 0.75) {
      widthPercentage = 62;
    } else if (aspectRatio <= 1) {
      widthPercentage = 72;
    } else if (aspectRatio <= 1.25) {
      widthPercentage = 82;
    } else if (aspectRatio <= 1.5) {
      widthPercentage = 88;
    } else if (aspectRatio <= 1.75) {
      widthPercentage = 93;
    }
  } else if (widthVariant === "create") {
    if (aspectRatio <= 0.75) {
      widthPercentage = 70;
    } else if (aspectRatio <= 1) {
      widthPercentage = 80;
    } else if (aspectRatio <= 1.25) {
      widthPercentage = 90;
    } else if (aspectRatio <= 1.5) {
      widthPercentage = 90;
    } else if (aspectRatio <= 1.75) {
      widthPercentage = 100;
    }
  } else if (widthVariant === "view") {
    let widthPercentages = { XXXS: 100, XXS: 100, XS: 100 };
    if (widthPercentages[breakpoint]) {
      return widthPercentages[breakpoint];
    }
    if (aspectRatio <= 0.75) {
      widthPercentage = 70;
    } else if (aspectRatio <= 1) {
      widthPercentage = 70;
    } else if (aspectRatio <= 1.25) {
      widthPercentage = 80;
    } else if (aspectRatio <= 1.5) {
      widthPercentage = 90;
    } else if (aspectRatio <= 1.75) {
      widthPercentage = 95;
    }
  } else if (widthVariant === "edit") {
    let widthPercentages = { XXXS: 100, XXS: 100, XS: 100 };
    if (widthPercentages[breakpoint]) {
      return widthPercentages[breakpoint];
    }
    if (aspectRatio <= 0.75) {
      widthPercentage = 70;
    } else if (aspectRatio <= 1) {
      widthPercentage = 70;
    } else if (aspectRatio <= 1.25) {
      widthPercentage = 80;
    } else if (aspectRatio <= 1.5) {
      widthPercentage = 90;
    } else if (aspectRatio <= 1.75) {
      widthPercentage = 95;
    }
  } else if (widthVariant === "suggestions") {
    let widthPercentages = { XXXS: 100, XXS: 100, XS: 100 };
    if (widthPercentages[breakpoint]) {
      return widthPercentages[breakpoint];
    }
    if (aspectRatio <= 0.75) {
      widthPercentage = 75;
    } else if (aspectRatio <= 1) {
      widthPercentage = 90;
    } else if (aspectRatio <= 1.25) {
      widthPercentage = 90;
    } else if (aspectRatio <= 1.5) {
      widthPercentage = 90;
    } else if (aspectRatio <= 1.75) {
      widthPercentage = 100;
    }
  }
  return widthPercentage;
};
